import type {
  CreativeWorksSearchDateRecord,
  CreativeWorksCatalogResultRaw,
} from 'api/creativeWorksSearch/interfaces';

/**
 * @module creativeWorksSearchParseUtils
 * Low-level parsing helpers for Creative Works search catalog payloads.
 * Strips HTML, normalizes URLs, extracts catalog item IDs from catalog links, and
 * derives display dates from copyright notices.
 */

const SAFE_ID = /^[a-zA-Z0-9][a-zA-Z0-9._/-]*$/;
const GHE_APPID_PARAM = /^[a-z0-9-]{8,}$/i;
const CATALOG_ITEM_PATH_PREFIX = '/item/';
const CATALOG_GHE_CASCADE_PATH = '/ghe/cascade/index.html';
const CATALOG_LCCN_HOST = 'lccn.loc.gov';

const safeDecodePathSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

const isCatalogHostname = (hostname: string): boolean => {
  const host = hostname.toLowerCase();
  return (
    host === 'loc.gov' || host === 'www.loc.gov' || host.endsWith('.loc.gov')
  );
};

const extractItemIdFromPathname = (pathname: string): string | null => {
  const normalizedPath = pathname.toLowerCase();
  if (!normalizedPath.startsWith(CATALOG_ITEM_PATH_PREFIX)) {
    return null;
  }
  const segment = pathname
    .slice(CATALOG_ITEM_PATH_PREFIX.length)
    .split('/')[0]
    ?.trim()
    .replace(/\/+$/, '');
  return segment ? safeDecodePathSegment(segment) : null;
};

const extractGheIdFromUrl = (parsed: URL): string | null => {
  if (
    !isCatalogHostname(parsed.hostname) ||
    parsed.pathname.toLowerCase() !== CATALOG_GHE_CASCADE_PATH
  ) {
    return null;
  }
  const appid = parsed.searchParams.get('appid')?.trim().toLowerCase() ?? '';
  if (!GHE_APPID_PARAM.test(appid)) {
    return null;
  }
  return `ghe-${appid}`;
};

/** Canonical base URL for a catalog catalog item detail page. */
export const CREATIVE_WORKS_CATALOG_ITEM_BASE_URL = 'https://www.loc.gov/item/';

/**
 * Converts catalog field values to plain text.
 * @param value - Raw catalog field (string, number, or nested value).
 * @returns Sanitized single-line text, or empty string when input is empty.
 */
export const plainTextSanitize = (value: unknown): string => {
  let text = value == null ? '' : String(value);
  if (!text.trim()) {
    return '';
  }
  text = text.replace(/<[^>]*>/g, ' ');
  text = text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&amp;/gi, '&')
    .replace(/\u00a0/g, ' ');
  return text.replace(/\s+/g, ' ').trim();
};

/**
 * Sanitizes each entry in an array and drops empty strings.
 * @param values - Array of raw catalog field values.
 * @returns Non-empty sanitized strings.
 */
export const plainTextSanitizeArray = (values: unknown[]): string[] =>
  values.map((value) => plainTextSanitize(value)).filter(Boolean);

/** Max length for single-word card titles before truncation. */
export const CREATIVE_WORKS_SEARCH_CARD_TITLE_MAX_LENGTH = 72;

const isSingleWordTitle = (text: string): boolean => !/\s/.test(text);

const readableTitleFromUrl = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    const segments = parsed.pathname.split('/').filter(Boolean);
    if (segments.length > 0) {
      const lastSegment = decodeURIComponent(segments[segments.length - 1]);
      if (lastSegment) {
        return lastSegment;
      }
    }
    return parsed.hostname.replace(/^www\./i, '');
  } catch {
    return null;
  }
};

const truncateSingleWordTitle = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength - 1)}…`;
};

/**
 * Shortens single-word titles (often raw URLs) for copyright search result cards.
 * @param title - Sanitized title from catalog.
 * @returns Display title; multi-word titles are unchanged.
 */
export const formatCreativeWorksSearchCardTitle = (title: string): string => {
  const trimmed = title.trim();
  if (!trimmed || !isSingleWordTitle(trimmed)) {
    return trimmed;
  }
  if (trimmed.length <= CREATIVE_WORKS_SEARCH_CARD_TITLE_MAX_LENGTH) {
    return trimmed;
  }

  const fromUrl = readableTitleFromUrl(trimmed);
  if (fromUrl) {
    return truncateSingleWordTitle(
      fromUrl,
      CREATIVE_WORKS_SEARCH_CARD_TITLE_MAX_LENGTH
    );
  }

  return truncateSingleWordTitle(
    trimmed,
    CREATIVE_WORKS_SEARCH_CARD_TITLE_MAX_LENGTH
  );
};

/**
 * Ensures a URL is absolute and uses HTTPS.
 * @param url - Relative, protocol-relative, or absolute URL from catalog.
 * @returns Absolute HTTPS URL, or empty string when input is missing.
 */
export const urlAbsolutize = (url: unknown): string => {
  const value = url == null ? '' : String(url).trim();
  if (!value) {
    return '';
  }
  try {
    const parsed = new URL(
      value.startsWith('//') ? `https:${value}` : value,
      'https://www.loc.gov'
    );
    parsed.protocol = 'https:';
    return parsed.href;
  } catch {
    return '';
  }
};

/**
 * Pulls a catalog catalog item id from a user-provided string.
 * @param raw - Item URL, LCCN link, GHE cascade URL, or bare id.
 * @returns Catalog item id, or `null` when no id can be parsed.
 */
export const extractItemIdFromString = (raw: string): string | null => {
  let candidate = raw.trim();
  if (!candidate) {
    return null;
  }

  if (candidate.startsWith('//')) {
    candidate = `https:${candidate}`;
  }
  candidate = candidate.replace(/^http:\/\//i, 'https://');

  try {
    const parsed = new URL(candidate, 'https://www.loc.gov');

    const itemId = extractItemIdFromPathname(parsed.pathname);
    if (itemId) {
      return itemId;
    }

    if (parsed.hostname.toLowerCase() === CATALOG_LCCN_HOST) {
      const lccnSegment = parsed.pathname.replace(/^\/+/, '').split('/')[0];
      return lccnSegment?.trim() || null;
    }

    const gheId = extractGheIdFromUrl(parsed);
    if (gheId) {
      return gheId;
    }
  } catch {
    // Fall through to bare-id handling.
  }

  const bareId = candidate.replace(/^\/+|\/+$/g, '');
  if (
    bareId &&
    SAFE_ID.test(bareId) &&
    !bareId.includes('..') &&
    !bareId.includes('//')
  ) {
    return bareId;
  }

  return null;
};

/**
 * Extracts a catalog item id from a catalog search result row.
 * @param row - Raw search hit with `url` and/or `id`.
 * @returns Parsed catalog id, or `null` when the row has no link/id.
 */
export const extractItemIdFromSearchRow = (
  row: Pick<CreativeWorksCatalogResultRaw, 'url' | 'id'>
): string | null => {
  const raw = String(row.url || row.id || '').trim();
  return raw ? extractItemIdFromString(raw) : null;
};

/**
 * Checks whether an id is safe to embed in catalog item request URLs.
 * @param id - Candidate catalog id from route params or user input.
 * @returns `true` when the id is non-empty, alphanumeric, and has no path traversal.
 */
export const isSafeCreativeWorksSearchItemId = (id: string): boolean => {
  const value = id.trim();
  return (
    Boolean(value) &&
    SAFE_ID.test(value) &&
    !value.includes('..') &&
    !value.includes('//')
  );
};

/**
 * Builds the public catalog item page URL for a catalog id.
 * @param id - catalog catalog item id.
 * @returns Absolute item page URL with trailing slash.
 */
export const catalogItemUrl = (id: string): string =>
  `${CREATIVE_WORKS_CATALOG_ITEM_BASE_URL}${encodeURIComponent(id)}/`;

/** External catalog JSON field for control numbers (LCCN-style identifiers). */
const CATALOG_JSON_CONTROL_NUMBER_FIELD = 'library_of_congress_control_number';

/** Reads a catalog control number from a raw item payload. */
export const controlNumberFromCatalogItem = (
  item: Record<string, unknown>
): string => plainTextSanitize(item[CATALOG_JSON_CONTROL_NUMBER_FIELD]);

const formatFragment = (value: unknown): string => {
  if (typeof value === 'string') {
    return plainTextSanitize(value);
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const key = Object.keys(value as Record<string, unknown>)[0];
    return key ? plainTextSanitize(key) : '';
  }
  return plainTextSanitize(value);
};

/**
 * Joins catalog format field entries into a display label.
 * @param values - catalog format string, object map, array, or mixed shapes from API JSON.
 * @returns Comma-separated format label, or empty string.
 */
export const joinFormatFragments = (values: unknown): string => {
  if (values == null || values === '') {
    return '';
  }
  const entries = Array.isArray(values) ? values : [values];
  const fragments = entries
    .map((entry) => formatFragment(entry))
    .filter(Boolean);
  return fragments.join(', ');
};

/**
 * Returns the first non-empty sanitized line from a catalog text array field.
 * @param values - catalog notes, description, or similar array field.
 * @returns First sanitized line, or empty string.
 */
export const firstPlainLine = (values: unknown): string => {
  const lines = plainTextSanitizeArray(Array.isArray(values) ? values : []);
  return lines[0] || '';
};

/**
 * Returns the first HTTPS image URL from a catalog `image_url` array.
 * @param values - catalog `image_url` field (string or array).
 * @returns First absolutized image URL, or empty string.
 */
export const firstAbsolutizedImage = (values: unknown): string => {
  if (values == null || values === '') {
    return '';
  }
  const entries = Array.isArray(values) ? values : [values];
  for (const entry of entries) {
    const url = urlAbsolutize(entry);
    if (url) {
      return url;
    }
  }
  return '';
};

const COPYRIGHT_NOTICE_PATTERNS = [
  /©\s*(\d{4})\b/,
  /\(c\)\s*(\d{4})\b/i,
  /\bcopyright(?:\s+date)?\s*:?\s*(\d{4})\b/i,
] as const;

const COPYRIGHT_SUFFIX_PATTERN = /\bc\s*(\d{4})\b\.?\s*$/i;

const createdPublishedLines = (
  record: CreativeWorksSearchDateRecord
): string[] => {
  const lines: string[] = [];
  const append = (source: unknown) => {
    if (Array.isArray(source)) {
      for (const entry of source) {
        lines.push(String(entry));
      }
    } else if (source != null && source !== '') {
      lines.push(String(source));
    }
  };

  append(record.created_published);
  if (record.item) {
    append(record.item.created_published);
  }

  return lines;
};

const copyrightYearFromCreatedPublished = (lines: string[]): string => {
  for (const line of lines) {
    const text = plainTextSanitize(line);
    if (!text) {
      continue;
    }
    for (const pattern of COPYRIGHT_NOTICE_PATTERNS) {
      const match = text.match(pattern);
      if (match?.[1]) {
        return match[1];
      }
    }
  }

  for (const line of lines) {
    const text = plainTextSanitize(line);
    const match = text.match(COPYRIGHT_SUFFIX_PATTERN);
    if (match?.[1]) {
      return match[1];
    }
  }

  return '';
};

/**
 * Best-effort copyright or registration year for search result cards.
 * Prefers © / (c) lines in `created_published`, then `event_date`, then `date`.
 * @param record - Raw catalog search row or nested `item` object.
 * @returns Year or date string for display, or empty string.
 */
export const extractCopyrightRegistrationDate = (
  record: CreativeWorksSearchDateRecord
): string => {
  const nested = record.item;

  const fromCreatedPublished = copyrightYearFromCreatedPublished(
    createdPublishedLines(record)
  );
  if (fromCreatedPublished) {
    return fromCreatedPublished;
  }

  const eventDate = plainTextSanitize(record.event_date ?? nested?.event_date);
  if (eventDate) {
    return eventDate;
  }

  return plainTextSanitize(record.date ?? nested?.date);
};
